import {
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core'
import React from 'react'
import { PARAMETER_INPUT_TYPE } from '../constants'
import { useBestPracticeManagementPanelStyles } from '../styles/useBestPracticeManagementPanelStyles'
import {
    parameterConfig,
    ParameterInputType,
    ParameterKey,
    ParameterOptionsMap,
    ParameterSelectedState,
    ParameterToggleState,
} from '../types/bpManagement.types'

interface ParameterListSectionProps {
    firmOptions: ParameterOptionsMap
    parameterToggles: ParameterToggleState
    parameterSelected: ParameterSelectedState
    onToggle: (key: ParameterKey) => void
    onSelectedChange: (key: ParameterKey, values: string[]) => void
}

interface ParameterInputProps {
    inputType: ParameterInputType
    options: string[]
    selected: string[]
    className: string
    onChange: (values: string[]) => void
}

/** Renders the value-entry control for a parameter row, based on its input type. */
const ParameterInput = ({
    inputType,
    options,
    selected,
    className,
    onChange,
}: ParameterInputProps) => {
    if (inputType === PARAMETER_INPUT_TYPE.TEXT_FIELD) {
        return (
            <TextField
                value={selected[0] || ''}
                onChange={(e) => {
                    const val = e.target.value
                    onChange(val ? [val] : [])
                }}
                className={className}
                variant="outlined"
            />
        )
    }

    if (inputType === PARAMETER_INPUT_TYPE.MULTI_SELECT) {
        return (
            <Select
                multiple
                value={selected}
                onChange={(e) => onChange(e.target.value as string[])}
                className={className}
                variant="outlined"
            >
                {options.map((option) => (
                    <MenuItem key={option} value={option}>
                        {option}
                    </MenuItem>
                ))}
            </Select>
        )
    }

    return (
        <Select
            value={selected[0] || ''}
            onChange={(e) => {
                const val = e.target.value as string
                onChange(val ? [val] : [])
            }}
            className={className}
            variant="outlined"
        >
            {options.map((option) => (
                <MenuItem key={option} value={option}>
                    {option}
                </MenuItem>
            ))}
        </Select>
    )
}

export const ParameterListSection = ({
    firmOptions,
    parameterToggles,
    parameterSelected,
    onToggle,
    onSelectedChange,
}: ParameterListSectionProps) => {
    const classes = useBestPracticeManagementPanelStyles()

    return (
        <>
            {parameterConfig.map(
                ({ key, label, inputType = PARAMETER_INPUT_TYPE.SELECT }) => {
                    const allOptions = firmOptions[key]
                    const selected = parameterSelected[key]
                    const isTextField =
                        inputType === PARAMETER_INPUT_TYPE.TEXT_FIELD
                    // Text fields have no options list to gate on - the user
                    // supplies the value directly.
                    const hasOptions = isTextField || allOptions.length > 0
                    const isOn = parameterToggles[key]
                    return (
                        <div className={classes.parameterRow} key={key}>
                            <Typography className={classes.parameterLabel}>
                                {label}
                            </Typography>
                            {isOn && (
                                <ParameterInput
                                    inputType={inputType}
                                    options={allOptions}
                                    selected={selected}
                                    className={classes.select}
                                    onChange={(values) =>
                                        onSelectedChange(key, values)
                                    }
                                />
                            )}
                            <Switch
                                checked={isOn}
                                onChange={() => onToggle(key)}
                                color="primary"
                                size="small"
                                disabled={!hasOptions}
                                inputProps={{ 'aria-label': `${label} Switch` }}
                            />
                        </div>
                    )
                }
            )}
        </>
    )
}
